import { useEffect, useState, useCallback } from "react";
import { X, Trash2 } from "lucide-react";
import { fullUrl } from "@/components/app/ImageUploader";
import api from "@/lib/api";
import { toast } from "sonner";

export default function StoriesViewer({ posts, startIndex = 0, canDelete = () => false, onClose, onDeleted }) {
  const [index, setIndex] = useState(startIndex);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const DURATION = 5000;

  const next = useCallback(() => {
    setProgress(0);
    setIndex((i) => (i + 1 >= posts.length ? i : i + 1));
  }, [posts.length]);

  const prev = useCallback(() => {
    setProgress(0);
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  useEffect(() => {
    if (paused) return;
    const started = Date.now();
    const t = setInterval(() => {
      const elapsed = Date.now() - started;
      const pct = Math.min(100, (elapsed / DURATION) * 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(t);
        if (index + 1 >= posts.length) {
          onClose?.();
        } else {
          next();
        }
      }
    }, 50);
    return () => clearInterval(t);
  }, [index, paused, next, posts.length, onClose]);

  if (!posts || !posts.length) return null;
  const p = posts[index];
  const src = fullUrl(p.image_url);

  const removeThis = async () => {
    if (!window.confirm("Apagar essa foto?")) return;
    try {
      await api.delete(`/events/${p.event_id}/posts/${p.post_id}`);
      toast.success("Foto removida.");
      onDeleted?.(p.post_id);
      if (posts.length <= 1) onClose?.();
      else if (index >= posts.length - 1) prev();
    } catch { toast.error("Não foi possível apagar."); }
  };

  const tapHandler = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < rect.width / 3) prev();
    else if (x > (rect.width * 2) / 3) next();
    else setPaused((s) => !s);
  };

  return (
    <div className="fixed inset-0 z-[2000] bg-black" data-testid="stories-viewer">
      <div className="absolute top-0 inset-x-0 flex gap-1 p-2 z-10">
        {posts.map((_, i) => (
          <div key={i} className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white transition-[width] duration-100" style={{ width: `${i < index ? 100 : i === index ? progress : 0}%` }} />
          </div>
        ))}
      </div>

      <div className="absolute top-4 inset-x-0 px-4 pt-3 flex items-center gap-3 z-10 pointer-events-none">
        <div className="h-9 w-9 rounded-full bg-white/10 overflow-hidden grid place-items-center text-sm font-semibold shrink-0">
          {p.avatar_url ? <img src={fullUrl(p.avatar_url)} alt="" className="w-full h-full object-cover" /> : (p.nickname?.[0] || "?").toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm truncate">{p.nickname}</div>
          <div className="text-white/60 text-[11px]">{new Date(p.created_at).toLocaleString("pt-BR")}</div>
        </div>
        <div className="flex items-center gap-2 pointer-events-auto">
          {canDelete(p) && (
            <button onClick={removeThis} className="h-9 w-9 rounded-full bg-black/60 grid place-items-center" data-testid="story-delete-button"><Trash2 className="w-4 h-4 text-white" /></button>
          )}
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-black/60 grid place-items-center" data-testid="story-close-button"><X className="w-5 h-5 text-white" /></button>
        </div>
      </div>

      <button
        className="absolute inset-0 w-full h-full"
        onClick={tapHandler}
        onMouseDown={()=>setPaused(true)}
        onMouseUp={()=>setPaused(false)}
        onTouchStart={()=>setPaused(true)}
        onTouchEnd={()=>setPaused(false)}
        aria-label="Avançar"
        data-testid="story-tap-area"
      >
        <img src={src} alt="" className="w-full h-full object-contain" />
      </button>

      {p.caption && (
        <div className="absolute bottom-6 inset-x-0 px-6 text-center text-white text-sm font-medium drop-shadow pointer-events-none">
          {p.caption}
        </div>
      )}
    </div>
  );
}
